import { Button, Table } from '@odin-labs/components';
import { useGetSiteConnectorsQuery } from 'apollo/generated/client-operations';
import { PlusIcon } from 'components/icons';
import { NewHeader } from 'components/header/NewHeader';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import React from 'react';
import { ConnectorColumn, getColumns } from './ConnectorsTab.tables';
import {
  ConnectorCatalogDrawer,
  ConnectorDetailsDrawer,
  ConnectorSetupDrawer,
} from './drawers';
import type { ConnectorCatalogItem } from './connectorTypes';

export function ConnectorsTab({
  jobsiteId: siteId,
  onTabApiChange,
  onIsDirtyChange,
}: JobsiteConfigTabProps): React.ReactElement {
  const [isCatalogOpen, setIsCatalogOpen] = React.useState(false);
  const [setupCatalogItem, setSetupCatalogItem] =
    React.useState<ConnectorCatalogItem | null>(null);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = React.useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = React.useState<
    string | null
  >(null);
  const { data, loading, error, refetch } = useGetSiteConnectorsQuery({
    variables: { siteId },
    fetchPolicy: 'no-cache',
  });
  const connectors = data?.getJobsite.connectors ?? [];
  const selectedConnector =
    connectors.find((connector) => connector.id === selectedConnectorId) ??
    null;
  const columns = React.useMemo<ConnectorColumn[]>(() => getColumns(), []);
  React.useEffect(() => {
    onTabApiChange?.({ refetchData: refetch, loading, jobsiteName: null });
  }, [loading, onTabApiChange, refetch]);
  React.useEffect(() => onIsDirtyChange(false), [onIsDirtyChange]);

  if (error) return <LoadingError error={error} />;

  return (
    <>
      <div className="odin-flex odin-flex-col odin-gap-y-6 odin-p-6">
        <NewHeader
          title="Connectors"
          size="md"
          actionsProps={{
            headerActions: null,
            children: (
              <Button
                icon={PlusIcon}
                text="Add connector"
                size="sm"
                onClick={() => {
                  setSetupCatalogItem(null);
                  setIsCatalogOpen(true);
                }}
              />
            ),
          }}
        />
        <Table
          loading={loading}
          columns={columns}
          data={connectors}
          onRowClick={({ data: selected }) => {
            setSelectedConnectorId(selected.id);
            setIsDetailsDrawerOpen(true);
          }}
          remote
          disableGlobalFilter
          disableSortBy
        />
      </div>

      {!setupCatalogItem && (
        <ConnectorCatalogDrawer
          isOpen={isCatalogOpen}
          siteId={siteId}
          onSelect={setSetupCatalogItem}
          onClose={() => {
            setIsCatalogOpen(false);
            setSetupCatalogItem(null);
          }}
        />
      )}
      {setupCatalogItem && (
        <ConnectorSetupDrawer
          isOpen={isCatalogOpen}
          siteId={siteId}
          catalogItem={setupCatalogItem}
          onClose={() => {
            setIsCatalogOpen(false);
            setSetupCatalogItem(null);
          }}
          onCreated={refetch}
          onBack={() => setSetupCatalogItem(null)}
        />
      )}
      <ConnectorDetailsDrawer
        open={isDetailsDrawerOpen && Boolean(selectedConnector)}
        connector={selectedConnector}
        onClose={() => setIsDetailsDrawerOpen(false)}
        onAfterLeave={() => {
          setIsDetailsDrawerOpen(false);
          setSelectedConnectorId(null);
        }}
        onUpdated={refetch}
      />
    </>
  );
}
